import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../core/auth'
import { Plus, Users, X } from 'lucide-react'
import type { NtCostItem, NtCourse, NtRoster, NtTerm } from '../../core/types'
import {
  BLANK_COURSE, createCourse, saveCourse, deleteCourse,
  STATUS_LABEL, unplannedParts, planObservations, costTotal,
} from './model'
import { DayPlanner } from './DayPlanner'
import { CourseExemplarPanel } from './CourseExemplars'
import { ArtifactEditor, realOptions } from './ArtifactOptions'
import { fmtDate } from './Overview'
import { Text, Area, Select } from '../../ui/Field'
import { ErrorState } from '../../ui/States'

/**
 * The teacher's whole side of Norse Term: propose a course, plan the four
 * days, see who is coming.
 *
 * One screen rather than the prototype's four tabs, because a teacher touches
 * this maybe six times a year and should not have to remember where anything
 * lives. The day planner is the part they will actually spend an hour on.
 */
export function MyCourse({ term, mine, rosters, uid, name, email }: {
  term: NtTerm
  mine: NtCourse[]
  rosters: NtRoster[]
  uid: string
  name: string
  email: string
}) {
  // Which course is open lives in the address, so a checklist item can open it
  // directly (?course=<id>, and &focus=plan to land on the day planner).
  const [params, setParams] = useSearchParams()
  const editing = params.get('course')
  const focus = params.get('focus')
  const examples = params.get('examples') === '1'
  const setEditing = (id: string | null) => setParams((p) => {
    const n = new URLSearchParams(p)
    n.delete('phase'); n.delete('tab'); n.delete('focus'); n.delete('examples')
    if (id) n.set('course', id); else n.delete('course')
    return n
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const { preview } = useAuth()
  // In a "View as…" preview nothing can be created, so "Create course overview" opens
  // a practice draft that lives only in this tab — enough to walk a room
  // through every step of writing a course without anything being saved.
  const [practice, setPractice] = useState<NtCourse | null>(null)

  const start = async () => {
    if (preview) {
      const draft: NtCourse = { id: 'practice', ...BLANK_COURSE(term.id, uid, name, email), title: '' }
      setPractice(draft); setEditing(draft.id); return
    }
    setBusy(true); setErr(null)
    try {
      const id = await createCourse({ ...BLANK_COURSE(term.id, uid, name, email), title: 'Untitled course' })
      setEditing(id)
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  const open = mine.find((c) => c.id === editing) ?? (practice?.id === editing ? practice : undefined)
  if (open) {
    return <CourseEditor course={open} rosters={rosters} focus={focus} practice={open === practice}
      due={term.deadlines.courses} onClose={() => setEditing(null)} />
  }

  return (
    <>
      {err && <ErrorState body={`Could not create the course. ${err}`} />}

      {mine.length === 0 ? (
        <>
          {/* A teacher's first visit: one line of direction, not a blank form.
              The full how-it-works lives on its own page. */}
          <section className="card" style={{ marginTop: 'var(--s-2)' }}>
            <h2 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-1)' }}>You haven&apos;t started a course yet</h2>
            <p className="ov__p" style={{ margin: 0 }}>
              Read a couple of the example courses below, then create your course overview. It saves as you go
              {term.deadlines.courses ? <>, and it&apos;s due <strong>{fmtDate(term.deadlines.courses, { month: 'long', day: 'numeric' })}</strong></> : null}.
              {' '}<Link to="/norseterm/info">New to Norse Term? Read what&apos;s needed from you →</Link>
            </p>
          </section>
          <CourseExemplarPanel key={String(examples)} defaultOpen={examples} />
          <button className="btn" onClick={start} disabled={busy} style={{ marginTop: 'var(--s-3)' }}>
            {busy ? 'Opening…' : 'Create course overview'}
          </button>
          <p className="opt__hint" style={{ textAlign: 'center' }}>
            It saves as you go, so you don&apos;t have to finish it today.
          </p>
        </>
      ) : (
        <>
          {examples && <CourseExemplarPanel defaultOpen />}
          {mine.map((c) => {
            const roster = rosters.find((r) => r.courseId === c.id)
            return (
              <section key={c.id} className="card" style={{ marginTop: 'var(--s-3)' }}>
                <div className="pagehead">
                  <div>
                    <h2 style={{ fontSize: 'var(--t-md)', margin: 0 }}>{c.title || 'Untitled course'}</h2>
                    <p style={{ color: 'var(--ink-muted)', fontSize: 'var(--t-sm)', margin: '2px 0 0' }}>
                      {[c.room, c.block ? `${c.block} block` : 'Block not assigned yet'].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <span className={`pill ${c.status === 'approved' ? 'pill--good' : c.status === 'returned' ? 'pill--serious' : ''}`}>
                    {STATUS_LABEL[c.status]}
                  </span>
                </div>

                {/* The reviewer's note, if there is one. Loud, because a course
                    sent back in January and not noticed until March is a course
                    that does not run. */}
                {c.status === 'returned' && c.feedback && (
                  <div className="notice notice--warning" style={{ marginTop: 'var(--s-3)' }}>
                    <span aria-hidden="true">⚠</span>
                    <div><strong>Sent back:</strong> {c.feedback}</div>
                  </div>
                )}

                <p style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)', margin: 'var(--s-3) 0' }}>
                  {c.desc || 'No description yet.'}
                </p>

                <div className="dl">
                  <div><div className="dl__k">Seats</div><div className="dl__v">{c.min}–{c.cap}</div></div>
                  <div><div className="dl__k">Cost</div><div className="dl__v">{c.costItems.length ? `$${costTotal(c.costItems)}` : 'None'}</div></div>
                  <div><div className="dl__k">Unplanned</div><div className="dl__v">{unplannedParts(c.plan)} of 6 parts</div></div>
                </div>
                {c.costItems.length > 0 && (
                  <ul className="opt__list" style={{ marginTop: 'var(--s-2)' }}>
                    {c.costItems.map((it, i) => (
                      <li key={i} className="work__row">
                        <span>{it.label || 'Untitled item'}</span>
                        <span>${it.amount}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {roster && (
                  <div className="notice notice--info" style={{ margin: 'var(--s-3) 0 0' }}>
                    <Users size={15} aria-hidden="true" />
                    <div>
                      <strong>{roster.students.length} students placed.</strong>{' '}
                      {roster.students.slice(0, 4).map((s) => s.name).join(', ')}
                      {roster.students.length > 4 && `, +${roster.students.length - 4} more`}
                    </div>
                  </div>
                )}

                <div className="opt__bar">
                  <button className="btn" onClick={() => setEditing(c.id)}>
                    {c.status === 'approved' ? 'View your course' : 'Keep planning this course'}
                  </button>
                </div>
              </section>
            )
          })}

          <button className="btn btn--ghost" onClick={start} disabled={busy}
            style={{ marginTop: 'var(--s-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Plus size={16} aria-hidden="true" /> Create another course overview
          </button>
        </>
      )}
    </>
  )
}

function CourseEditor({ course, rosters, focus, practice = false, due, onClose }: {
  course: NtCourse
  rosters: NtRoster[]
  focus: string | null
  /** The course deadline, ISO, or '' when none is set. */
  due: string
  /** A preview's practice draft: everything works except saving, which says what would happen. */
  practice?: boolean
  onClose: () => void
}) {
  const [practiceNote, setPracticeNote] = useState<string | null>(null)
  // Two steps, not one long page: what the course is, then the three days.
  // ?focus=plan (from the checklist) opens on the second.
  const [step, setStep] = useState<'about' | 'plan'>(focus === 'plan' ? 'plan' : 'about')
  useEffect(() => { if (focus === 'plan') setStep('plan') }, [focus])
  const top = useRef<HTMLButtonElement>(null)
  const go = (next: 'about' | 'plan') => {
    setStep(next)
    top.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  // A copy of every edit, kept on this device the moment it is typed: a
  // reload can tear the page down before a save is recorded, and this copy
  // survives it. Put back on open only if the course has not been saved since
  // (`base`), so an old copy can never overwrite newer work from elsewhere.
  const backupKey = `sailnorse.courseDraft.${course.id}`
  const savedStamp = (course.updatedAt as unknown as { toMillis?: () => number } | undefined)?.toMillis?.() ?? 0
  const [restored] = useState<NtCourse | null>(() => {
    if (practice || course.status === 'approved') return null
    try {
      const b = JSON.parse(localStorage.getItem(backupKey) ?? 'null') as { f: NtCourse; base: number } | null
      return b && b.base === savedStamp ? { ...course, ...b.f, id: course.id, status: course.status } : null
    } catch { return null }
  })
  const [f, setF] = useState<NtCourse>(restored ?? course)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const set = <K extends keyof NtCourse>(k: K, v: NtCourse[K]) => setF((p) => ({ ...p, [k]: v }))

  // Autosave. A teacher writes this over several sittings and between
  // classes; a draft lost to a closed tab is a course that does not get
  // proposed. Every edit saves itself a moment after typing stops, and a
  // pending save is flushed on the way out rather than dropped.
  const latest = useRef(f)
  latest.current = f
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autosave = !practice && course.status !== 'approved'
  const write = () => {
    const c = latest.current
    return saveCourse(course.id, { ...c, artifactOptions: realOptions(c.artifactOptions) })
      .then(() => {
        setSavedAt(new Date()); setErr(null)
        // Saved: the device copy is no longer needed, unless they typed more meanwhile.
        if (latest.current === c) { try { localStorage.removeItem(backupKey) } catch { /* fine */ } }
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
  }
  const flush = () => {
    if (!pending.current) return
    clearTimeout(pending.current); pending.current = null
    void write()
  }
  const cancel = () => { if (pending.current) { clearTimeout(pending.current); pending.current = null } }
  useEffect(() => {
    if (f === course || !autosave) return   // nothing typed yet
    try { localStorage.setItem(backupKey, JSON.stringify({ f, base: savedStamp })) } catch { /* private window: autosave still runs */ }
    if (pending.current) clearTimeout(pending.current)
    pending.current = setTimeout(() => { pending.current = null; void write() }, 1200)
  }, [f])  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => flush(), [])  // eslint-disable-line react-hooks/exhaustive-deps
  // Leaving the page itself (closing the tab, reloading, switching apps or
  // locking a phone) never unmounts anything, so save then too. "hidden"
  // comes first and leaves time for the write to be queued on the device;
  // pagehide catches a close that skips it.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') flush() }
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('pagehide', flush)
    return () => { document.removeEventListener('visibilitychange', onHide); window.removeEventListener('pagehide', flush) }
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps
  const close = () => { flush(); onClose() }

  // Approved courses are frozen — the rules enforce it too. Students ranked
  // from the catalog, so what they read has to still be true on Tuesday.
  const locked = course.status === 'approved'
  const roster = rosters.find((r) => r.courseId === course.id)
  const notes = planObservations(f)
  const overviewDone = !!f.title.trim() && !!f.desc.trim() && realOptions(f.artifactOptions).length > 0

  const save = async (submit?: boolean) => {
    if (practice) {
      setPracticeNote(submit
        ? 'In a real account, this is where the course goes to an administrator for review, and the teacher’s checklist ticks “Send your course for review”. Nothing was saved.'
        : 'In a real account, the draft would now be saved. Only the teacher can see it until they submit. Nothing was saved.')
      return
    }
    cancel()
    setBusy(true); setErr(null)
    try {
      // Empty rows are a half-typed thought, not an option.
      await saveCourse(course.id, { ...f, artifactOptions: realOptions(f.artifactOptions) }, submit)
      setSavedAt(new Date())
      if (submit) onClose()
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  const remove = () => {
    cancel()   // a save landing after the delete would fail on a missing doc
    deleteCourse(course.id).then(onClose).catch((e) => setErr(e instanceof Error ? e.message : String(e)))
  }

  return (
    <>
      <button ref={top} className="backlink" onClick={close}
        style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0 }}>
        ← My courses
      </button>

      {/* Sent back: the reviewer's note is the first thing on the page, since
          the bell links straight here and the note is the whole point. */}
      {course.status === 'returned' && course.feedback && (
        <div className="notice notice--warning" role="status">
          <span aria-hidden="true">⚠</span>
          <div>
            <strong>Sent back for revision.</strong> {course.feedback}
            <div className="opt__hint" style={{ margin: '4px 0 0' }}>
              Make the changes, then press <strong>Send for review</strong> again (on the Plan the three days step).
            </div>
          </div>
        </div>
      )}

      {/* How this works, said once at the top, so nobody has to guess whether
          leaving the page loses their work or sends it to the principal. */}
      {!locked && !practice && course.status !== 'returned' && (
        <div className="notice notice--info">
          <span aria-hidden="true">i</span>
          <div>
            <strong>Your work saves automatically.</strong> Leave any time and pick up where you left off.
            {course.status === 'submitted'
              ? ' This course is with a reviewer; changes you make now are saved to it too.'
              : <> Nobody reviews it until you press <strong>Send for review</strong>{due ? <>, due <strong>{fmtDate(due, { month: 'long', day: 'numeric' })}</strong></> : null}.</>}
          </div>
        </div>
      )}

      {locked && (
        <div className="notice notice--info">
          <span aria-hidden="true">i</span>
          <div>
            Approved and in the catalog. Students make their course selections from what they read here, so it is
            locked. Ask whoever reviewed it to send it back if something has to change.
          </div>
        </div>
      )}
      {practice && (
        <div className="notice notice--info">
          <span aria-hidden="true">i</span>
          <div><strong>Practice course.</strong> Fill in anything: every field, the week's plan, the
            buttons. It lives only in this preview and is never saved.</div>
        </div>
      )}
      {err && <ErrorState body={`Not saved. ${err}`} />}

      <CourseSteps step={step} onStep={go} overviewDone={overviewDone} unplanned={unplannedParts(f.plan)} />

      {step === 'about' ? (
        <>
          {/* Beside the form, not in the catalog: examples to read while writing. */}
          {!locked && <CourseExemplarPanel />}

          <Text label="Course title" value={f.title} onChange={(v) => set('title', v)} required
            help="What a fifteen-year-old sees in the catalog. Be specific: 'Low & Slow: Backyard Barbecue' beats 'Cooking'." />
          <Area label="Description" value={f.desc} onChange={(v) => set('desc', v)} required />
          <Text label="Room or location" value={f.room} onChange={(v) => set('room', v)} />

          <div className="grid2">
            <Text label="Minimum" type="number" value={String(f.min)} onChange={(v) => set('min', Number(v) || 0)}
              help="Below this it usually gets cancelled." />
            <Text label="Cap" type="number" value={String(f.cap)} onChange={(v) => set('cap', Number(v) || 1)} />
          </div>
          <CostItems items={f.costItems} readOnly={locked} onChange={(v) => set('costItems', v)} />
          <ArtifactEditor options={f.artifactOptions ?? []} readOnly={locked} onChange={(v) => set('artifactOptions', v)} />

          <Select label="Block you would prefer" value={blockLabel(f.constraint)}
            onChange={(v) => set('constraint', v === 'Morning' ? 'AM' : v === 'Afternoon' ? 'PM' : 'none')}
            options={['Either is fine', 'Morning', 'Afternoon']}
            help="A preference, not a decision. The coordinator balances the whole schedule." />

          <Area label="What has to be arranged" value={f.needs} onChange={(v) => set('needs', v)}
            placeholder="Materials, transport, volunteers, food-safety forms…" />

          {roster && <Roster roster={roster} />}

          <div className="opt__bar">
            <button className="btn" onClick={() => go('plan')}>Next: plan the three days →</button>
          </div>
          {!locked && course.status === 'draft' && (
            <button className="btn btn--ghost" style={{ marginTop: 'var(--s-4)' }}
              onClick={() => (practice ? onClose() : remove())}>
              Delete this draft
            </button>
          )}
        </>
      ) : (
        <>
          {/* course.block wins once the coordinator has assigned one; until then this
              follows the teacher's own stated preference, defaulting to morning. */}
          <DayPlanner plan={f.plan} readOnly={locked} onChange={(plan) => set('plan', plan)}
            session={(course.block ?? f.constraint) === 'PM' ? 1 : 0}
            basis={course.block ? 'assigned' : f.constraint === 'none' ? 'either' : 'preferred'} />

          {/* Observations, not a gate. A welding course that is hands-on every hour
              is fine, and a machine should not tell a teacher otherwise. */}
          {notes.length > 0 && !locked && (
            <div className="notice notice--info" style={{ marginTop: 'var(--s-4)' }}>
              <span aria-hidden="true">i</span>
              <div>
                <strong>Worth a look before you send it:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {notes.map((n) => <li key={n}>{n}</li>)}
                </ul>
              </div>
            </div>
          )}

          {!locked && (
            <>
              {practiceNote && (
                <div className="notice notice--info" role="status">
                  <span aria-hidden="true">i</span><div><strong>Practice run.</strong> {practiceNote}</div>
                </div>
              )}
              <div className="opt__bar">
                <button className="btn btn--ghost" onClick={() => go('about')} disabled={busy}>
                  ← Course overview
                </button>
                <button className="btn" onClick={() => save(true)} disabled={busy || !overviewDone}>
                  {busy ? 'Sending…' : 'Send for review'}
                </button>
              </div>
              {!overviewDone && (
                <p className="opt__hint" style={{ textAlign: 'center' }}>
                  Before you send it, the course overview needs a title, a description and at least one
                  artifact option.
                </p>
              )}
            </>
          )}
        </>
      )}

      {savedAt && !locked && (
        <p className="opt__hint" style={{ textAlign: 'center' }} role="status">
          All changes saved · {savedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </p>
      )}
    </>
  )
}

/** The two steps of writing a course, as tabs across the top of the editor. */
function CourseSteps({ step, onStep, overviewDone, unplanned }: {
  step: 'about' | 'plan'
  onStep: (s: 'about' | 'plan') => void
  overviewDone: boolean
  unplanned: number
}) {
  const steps = [
    { k: 'about' as const, n: 1, label: 'Course overview', done: overviewDone },
    { k: 'plan' as const, n: 2, label: 'Plan the three days', done: unplanned === 0,
      sub: unplanned ? `${unplanned} of 6 parts to plan` : 'All planned' },
  ]
  return (
    <nav className="csteps" aria-label="Writing your course">
      {steps.map((x) => (
        <button key={x.k} className={`csteps__step${step === x.k ? ' is-on' : ''}`}
          aria-current={step === x.k ? 'step' : undefined} onClick={() => onStep(x.k)}>
          <span className={`csteps__num${x.done ? ' is-done' : ''}`} aria-hidden="true">{x.done ? '✓' : x.n}</span>
          <span>
            <span className="csteps__label">{x.label}</span>
            {x.sub && <span className="csteps__sub">{x.sub}</span>}
          </span>
        </button>
      ))}
    </nav>
  )
}

function Roster({ roster }: { roster: NtRoster }) {
  return (
    <section className="card" style={{ marginTop: 'var(--s-4)' }}>
      <h3 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-2)' }}>Roster · {roster.students.length}</h3>
      <ul className="work">
        {roster.students.map((s) => (
          <li key={s.studentId} className="work__row">
            <div className="work__main">
              <div style={{ fontWeight: 610 }}>{s.name}</div>
              <div className="work__sub">Grade {s.grade}</div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

const blockLabel = (c: NtCourse['constraint']) =>
  c === 'AM' ? 'Morning' : c === 'PM' ? 'Afternoon' : 'Either is fine'

/**
 * What a course costs, itemized — a materials list a principal can actually
 * evaluate, not one number asking to be trusted. Not a per-student fee; the
 * office does not collect this, whoever approves the course does.
 */
function CostItems({ items, readOnly, onChange }: {
  items: NtCostItem[]
  readOnly?: boolean
  onChange: (next: NtCostItem[]) => void
}) {
  const total = costTotal(items)

  const update = (i: number, patch: Partial<NtCostItem>) => {
    const next = items.slice()
    next[i] = { ...next[i]!, ...patch }
    onChange(next)
  }
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i))
  const add = () => onChange([...items, { label: '', amount: 0 }])

  return (
    <div className="opt">
      <div className="opt__head">
        <h3 className="opt__label">Cost</h3>
        <span className="opt__count">{total ? `$${total}` : 'None'}</span>
      </div>
      <p className="opt__hint">
        Itemize it (materials, a guest fee, transport) rather than one number asking to be
        trusted. Not a per-student charge; the office does not collect this. Whoever approves
        the course signs off on it too.
      </p>
      {items.map((it, i) => (
        <div key={i} className="opt__row">
          <input className="opt__input" placeholder="What it's for" aria-label={`Cost item ${i + 1} label`}
            value={it.label} disabled={readOnly}
            onChange={(e) => update(i, { label: e.target.value })} />
          <input className="opt__input" type="number" placeholder="$" style={{ maxWidth: 100 }}
            aria-label={`Cost item ${i + 1} amount`} value={String(it.amount)} disabled={readOnly}
            onChange={(e) => update(i, { amount: Number(e.target.value) || 0 })} />
          {!readOnly && (
            <button type="button" className="opt__icon opt__icon--danger" aria-label={`Remove ${it.label || 'this item'}`}
              onClick={() => remove(i)}>
              <X size={15} aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
      {!readOnly && (
        <button type="button" className="plan__add" onClick={add}>
          <Plus size={14} aria-hidden="true" /> Add a cost item
        </button>
      )}
    </div>
  )
}
